/**
 * EditarRecursoModal
 * Modal para editar un recurso existente en el maestro
 */

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { tipoRecursoService } from '../../services/catalogoService';
import { claseRecursoService } from '../../services/onebaseService';
import '../../styles/modals.css';

const EditarRecursoModal = ({ isOpen, onClose, recurso, onSubmit }) => {
  const [nombre, setNombre] = useState('');
  const [unidad, setUnidad] = useState('');
  const [status, setStatus] = useState('activo');
  const [stock, setStock] = useState(false);
  const [stockMinimo, setStockMinimo] = useState('0');
  const [tipoRecurso, setTipoRecurso] = useState('');
  const [tiposRecurso, setTiposRecurso] = useState([]);
  const [claseRecurso, setClaseRecurso] = useState('');
  const [clasesRecurso, setClasesRecurso] = useState([]);
  const [loading, setLoading] = useState(false);

  // Cargar tipos de recurso y clases de recurso cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      cargarTiposRecurso();
      cargarClasesRecurso();
    }
  }, [isOpen]);

  const cargarTiposRecurso = async () => {
    try {
      const response = await tipoRecursoService.getAll();
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setTiposRecurso(data);
    } catch (error) {
      console.error('Error al cargar tipos de recurso:', error);
      toast.error('Error al cargar tipos de recurso');
    }
  };

  const cargarClasesRecurso = async () => {
    try {
      const response = await claseRecursoService.getAll({ status: 'activo' });
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setClasesRecurso(data);
    } catch (error) {
      console.error('Error al cargar clases de recurso:', error);
      toast.error('Error al cargar clases de recurso');
    }
  };

  useEffect(() => {
    if (recurso) {
      setNombre(recurso.nombre);
      setUnidad(recurso.unidad || 'Un');
      setStatus(recurso.status);
      setStock(recurso.stock || false);
      setStockMinimo(String(recurso.stock_minimo || 0));
      setTipoRecurso(recurso.tipo_recurso || '');
      setClaseRecurso(recurso.clase_recurso || '');
    }
  }, [recurso, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!nombre.trim()) {
      alert('Por favor ingresa un nombre para el recurso');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        nombre: nombre.trim(),
        unidad: unidad.trim() || 'unidad',
        status,
        stock,
        stock_minimo: parseFloat(stockMinimo) || 0,
        tipo_recurso: tipoRecurso || null,
        clase_recurso: claseRecurso || null
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !recurso) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Recurso</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label htmlFor="nombre">Nombre del Recurso *</label>
            <input
              id="nombre"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre del recurso"
              maxLength={200}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Estado</label>
            <select
              id="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={loading}
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
              <option value="archived">Archivado</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="unidad">Unidad de Medida *</label>
            <select
              id="unidad"
              value={unidad}
              onChange={(e) => setUnidad(e.target.value)}
              disabled={loading}
            >
              <option value="Hr">Horas</option>
              <option value="Un">Unidades</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="tipo_recurso">Tipo de Recurso</label>
            <select
              id="tipo_recurso"
              value={tipoRecurso}
              onChange={(e) => setTipoRecurso(e.target.value)}
              disabled={loading}
            >
              <option value="">Selecciona un tipo (opcional)</option>
              {tiposRecurso.map(tipo => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="clase_recurso">Clase de Recurso</label>
            <select
              id="clase_recurso"
              value={claseRecurso}
              onChange={(e) => setClaseRecurso(e.target.value)}
              disabled={loading}
            >
              <option value="">Selecciona una clase (opcional)</option>
              {clasesRecurso.map(clase => (
                <option key={clase.id} value={clase.id}>
                  {clase.codigo_completo || clase.codigo} - {clase.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={stock}
                onChange={(e) => setStock(e.target.checked)}
                disabled={loading}
              />
              <span>Requiere control de stock/inventario</span>
            </label>
          </div>

          {stock && (
            <div className="form-group">
              <label htmlFor="stock_minimo">Stock Mínimo por Defecto</label>
              <input
                id="stock_minimo"
                type="number"
                min="0"
                step="0.01"
                value={stockMinimo}
                onChange={(e) => setStockMinimo(e.target.value)}
                placeholder="Ej: 10"
                disabled={loading}
              />
              <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                Este será el stock mínimo por defecto al agregar este recurso a una bodega
              </small>
            </div>
          )}

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditarRecursoModal;
